import { useState, useCallback, useRef, useEffect } from 'react';
import type { ProjectTask } from '@/types/task';
import { TaskDot } from './TaskDot';
import { ArrowOverlay } from './ArrowOverlay';
import { DrawingCanvas } from './DrawingCanvas';
import { isLineType, lineTypes, type LineType } from '../lib/lineMath';
import type { useContainerDimensions } from '../hooks/useContainerDimensions';

export type AnnotationTask = ProjectTask;

/** Data produced when user clicks or draws to create a new task */
export interface NewTaskClickData {
  /** Percentage coordinates of the click/start point */
  coordX: number;
  coordY: number;
  /** For video: timestamp in ms */
  time?: number;
  /** Raw pixel coordinates (for arrow storage) */
  pixelCoords?: [number, number][];
  /** Line type if drawn (not just clicked) */
  taskType?: LineType;
  /** Container dimensions at time of drawing (for line preview) */
  containerWidth?: number;
  containerHeight?: number;
}

interface AnnotationLayerProps {
  /** Ref to the media container for dimension tracking */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Dimensions from useContainerDimensions */
  dimensions: ReturnType<typeof useContainerDimensions>['dimensions'];
  tasks: AnnotationTask[];
  selectedTask?: AnnotationTask | null;
  onSelectTask: (task: AnnotationTask | null) => void;
  onCreateTask?: (data: NewTaskClickData) => void;
  /** Current playback time in seconds (for video arrow visibility) */
  currentTime?: number;
  /** Whether user can create tasks (false for viewers/guests) */
  canCreate?: boolean;
  /** Media type affects coordinate interpretation */
  mediaType: 'video' | 'image';
  /** Whether the tasks tab is active (annotations only show when active) */
  tasksVisible: boolean;
  /**
   * Optional transform to convert stored task coords to display percentages.
   * Used by ImageViewer where coords are stored as natural pixels, not percentages.
   * If omitted, raw coord_x/coord_y are treated as percentages (video default).
   */
  coordTransform?: (task: AnnotationTask) => { x: number; y: number };
}

/** Convert task coords to percentage values for positioning */
function getTaskPercentCoords(task: AnnotationTask) {
  // Tasks store coords in coord_x/coord_y (from DynamoDB transform) or coordX/coordY
  const x = Number(task.coord_x ?? task.coordX ?? 0);
  const y = Number(task.coord_y ?? task.coordY ?? 0);
  return { x, y };
}

export function AnnotationLayer({
  containerRef,
  dimensions,
  tasks,
  selectedTask,
  onSelectTask,
  onCreateTask,
  currentTime,
  canCreate = false,
  mediaType,
  tasksVisible,
  coordTransform,
}: AnnotationLayerProps) {
  const [lineType, setLineType] = useState<LineType>(lineTypes.freeLine);
  const [isDrawing, setIsDrawing] = useState(false);

  const handleClick = useCallback(
    (pixelX: number, pixelY: number) => {
      if (!onCreateTask || !dimensions.width) return;
      const coordX = (pixelX / dimensions.width) * 100;
      const coordY = (pixelY / dimensions.height) * 100;
      onCreateTask({ coordX, coordY });
    },
    [onCreateTask, dimensions],
  );

  const handleDrawStart = useCallback(() => {
    setIsDrawing(true);
  }, []);

  const handleDrawEnd = useCallback(
    (coords: [number, number][]) => {
      setIsDrawing(false);
      if (!onCreateTask || !dimensions.width || coords.length < 2) return;
      const [startX, startY] = coords[0];
      const coordX = (startX / dimensions.width) * 100;
      const coordY = (startY / dimensions.height) * 100;
      onCreateTask({
        coordX,
        coordY,
        pixelCoords: coords,
        taskType: lineType,
        containerWidth: dimensions.width,
        containerHeight: dimensions.height,
      });
    },
    [onCreateTask, dimensions, lineType],
  );

  if (!tasksVisible) return null;

  return (
    <>
      {/* Drawing canvas for creating new tasks */}
      {canCreate && onCreateTask && (
        <DrawingCanvas
          containerRef={containerRef}
          onDrawEnd={handleDrawEnd}
          onDrawStart={handleDrawStart}
          onClick={handleClick}
        />
      )}

      {/* Task dots */}
      {tasks.map((task) => {
        const { x, y } = coordTransform ? coordTransform(task) : getTaskPercentCoords(task);
        if (!x && !y) return null;

        // For video arrow/line tasks: only show dot during timestamp+duration window
        if (
          mediaType === 'video' &&
          currentTime !== undefined &&
          task.timestamp != null &&
          isLineType(task.taskType)
        ) {
          const startSec = Number(task.timestamp) / 1000;
          const endSec = startSec + Number(task.duration ?? 1);
          if (currentTime < startSec - 0.1 || currentTime > endSec + 0.1) return null;
        }

        return (
          <TaskDot
            key={task.id}
            coordX={x}
            coordY={y}
            taskType={task.taskType}
            isSelected={selectedTask?.id === task.id}
            onClick={() => onSelectTask(task)}
            pointerEventsNone={isDrawing}
          />
        );
      })}

      {/* Arrow/line overlays */}
      {tasks
        .filter((t) => t.coords && t.coords !== false && isLineType(t.taskType))
        .map((task) => {
          // For video: only show arrow if current time is within range
          let visible = true;
          if (mediaType === 'video' && currentTime !== undefined && task.timestamp != null) {
            const startSec = Number(task.timestamp) / 1000;
            const endSec = startSec + Number(task.duration ?? 1);
            visible = currentTime >= startSec - 0.1 && currentTime <= endSec + 0.1;
          }

          return (
            <ArrowOverlay
              key={`arrow-${task.id}`}
              coords={task.coords!}
              type={task.taskType as LineType}
              viewBoxWidth={dimensions.width}
              viewBoxHeight={dimensions.height}
              visible={visible}
            />
          );
        })}
    </>
  );
}
